import { useEffect, useCallback, useRef } from "react";
import { useWebSocket } from "../Context/WebSocketContext";

export const useTrading = (baseCurrency, quoteCurrency, resolution = "1") => {
  const {
    subscribe,
    unsubscribe,
    registerHandler,
    unregisterHandler,
    isConnected, // ← ADD THIS
    isLoggedIn,
    asks,
    bids,
    trades,
    latestTradePrice,
    latestTradeType,
    latestPrices,
    setAsks,
    setBids,
    setTrades,
    setLatestTradePrice,
    setLatestTradeType,
    setLatestPrices,
  } = useWebSocket();

  const pair = `${baseCurrency}_${quoteCurrency}`;
  const obEvent = `OB.${pair}`;
  const rtEvent = `RT.${pair}`;
  const chEvent = `CH.${pair}.${resolution}`;

  // Use refs to track if we're currently subscribed
  const isSubscribedRef = useRef(false);

  // Memoize handlers to prevent recreation
  const handleOrderBook = useCallback(
    (data) => {
      if (data?.data) {
        const { asks: incomingAsks, bids: incomingBids } = data.data;

        setAsks(incomingAsks || []);
        setBids(incomingBids || []);
      }
    },
    [setAsks, setBids]
  );

  const handleRecentTrades = useCallback(
    (data) => {
      if (data?.data?.[0]) {
        const tradeData = data.data;
        setTrades(tradeData);
        setLatestTradePrice(tradeData[0].rate);
        setLatestTradeType(tradeData[0].execution_side);
        setLatestPrices(tradeData.slice(0, 10).map((trade) => trade.rate));
      }
    },
    [setTrades, setLatestTradePrice, setLatestTradeType, setLatestPrices]
  );

  const handleCandleData = useCallback((data) => {
    if (data?.data) {
      // console.log("Candle data received:", data.data);
    }
  }, []);

  // Register handlers - separate from subscription
  useEffect(() => {
    registerHandler(obEvent, handleOrderBook);
    registerHandler(rtEvent, handleRecentTrades);
    registerHandler(chEvent, handleCandleData);

    return () => {
      unregisterHandler(obEvent);
      unregisterHandler(rtEvent);
      unregisterHandler(chEvent);
    };
  }, [
    obEvent,
    rtEvent,
    chEvent,
    handleOrderBook,
    handleRecentTrades,
    handleCandleData,
    registerHandler,
    unregisterHandler,
  ]);

  // Subscribe/unsubscribe - WAIT FOR CONNECTION
  useEffect(() => {
    // ← KEY FIX: Don't subscribe until connected
    if (!isConnected) {
      return;
    }

    const eventsToSubscribe = [obEvent, rtEvent, chEvent];

    subscribe(eventsToSubscribe);
    isSubscribedRef.current = true;

    return () => {
      unsubscribe(eventsToSubscribe);
      isSubscribedRef.current = false;

      // Clear state on unmount
      setAsks([]);
      setBids([]);
      setTrades([]);
      setLatestTradePrice(null);
      setLatestTradeType(null);
      setLatestPrices(null);
    };
  }, [isConnected, obEvent, rtEvent, chEvent, subscribe, unsubscribe]); // ← Add isConnected dependency

  // Return trading data and pair information
  return {
    pair,
    asks,
    bids,
    trades,
    latestTradePrice,
    latestTradeType,
    latestPrices,
    isLoggedIn,
  };
};
